import { useState } from "react";

import useDebounce from "@/Common/CustomHooks/useDebounce";
import { IGetFlowsheetTemplateNameExistsResponse } from "@/Flowsheets/Api/Contracts/GetFlowsheetTemplateNameExists";
import { FlowsheetTemplateClient } from "@/Flowsheets/Api/FlowsheetTemplatesClient";
import { IUseQueryResponse, useQuery } from "./useQuery";

/**
 * Interface representing the result of the useIsDuplicateTemplateNameCheck hook.
 */
interface IUseIsDuplicateTemplateNameCheckResult extends IUseQueryResponse<IGetFlowsheetTemplateNameExistsResponse> {
    /**
     * Indicates if the template name is currently being debounced.
     */
    isDebouncing: boolean;
}

/**
 * Custom hook to check if a template name is a duplicate.
 * It uses a debounced value of the template name to avoid excessive API calls.
 * The check is performed when the template name changes.
 * @param templateName The name of the template to check for duplicates.
 * @returns The result of the duplicate check.
 */
function useGetFlowsheetTemplateNameExists(
    templateName: string,
    isPatientTemplate: boolean = false
): IUseIsDuplicateTemplateNameCheckResult {
    const debouncedTemplateName = useDebounce(templateName, 300);

    // Use the debounced value only if isPatientTemplate is false
    const effectiveTemplateName = isPatientTemplate ? templateName : debouncedTemplateName;

    const [lastDuplicateTemplateNameCheck, setLastDuplicateTemplateNameCheck] = useState<IGetFlowsheetTemplateNameExistsResponse | null>(null);

    const useQueryResult = useQuery({
        query: async () => {
            if (!effectiveTemplateName?.trim()?.length) {
                return null;
            }

            if (lastDuplicateTemplateNameCheck && lastDuplicateTemplateNameCheck.SuggestedName === effectiveTemplateName) {
                return lastDuplicateTemplateNameCheck;
            }

            const result = await FlowsheetTemplateClient.getFlowsheetTemplateNameExists(effectiveTemplateName, isPatientTemplate);

            result.throwIfError();

            setLastDuplicateTemplateNameCheck(result.data);

            return result.data;
        },
        queryKey: [effectiveTemplateName]
    });

    return {
        ...useQueryResult,
        isDebouncing: debouncedTemplateName !== effectiveTemplateName
    };
}

export default useGetFlowsheetTemplateNameExists;
